import React, { useState } from "react";

import { useTranslation } from "react-i18next";

import { LoadingComponent } from "components";

import { toastOption } from "global/types";

import SimpleToast from "components/SimpleToast";
import { BarStrongLevelProps } from "components/StrongBar/interface";

import { userCustomerAuthenticationServices } from "services/userCustomerAuthentication";

import { stringFormat } from "utils/stringFormat";

import { ChangePasswordStepOne } from "./constants";
import { IChangePassword } from "./interface";
import { LeftFormContainer, LoadingContainer } from "../styles";

export const ChangePassword: React.FC<IChangePassword> = ({
  guid,
  setChangedPassword,
  setSuccessMessageReturnAPI,
}) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<[boolean, boolean]>([
    false,
    false,
  ]);
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [statusPassword, setStatusPassword] =
    useState<BarStrongLevelProps["strongLevel"]>("weak");

  const [triggerToast, setTriggerToast] = useState<number>(0);
  const [toastMessage, setToastMessage] = useState("");
  const [typeToast, setTypeToast] = useState<toastOption>("warning");

  const [loading, setLoading] = useState(false);

  const onChangePassword = (event: string) => {
    setPassword(event);
    const [, strongLvlChange] = stringFormat.isStrongPassword(event);

    const lvlBar = convertLevelBar(strongLvlChange);
    setStatusPassword(lvlBar);
  };

  const convertLevelBar = (
    param: string,
  ): BarStrongLevelProps["strongLevel"] => {
    switch (param) {
      case "strong":
        return "strong";
      case "medium":
        return "medium";
      default:
        return "weak";
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const [passIsStrong] = stringFormat.isStrongPassword(password);
    const [passIsStrongConfirm] =
      stringFormat.isStrongPassword(confirmPassword);

    if (!passIsStrong || !passIsStrongConfirm) {
      setToastMessage(t("auth.changePassword.followRules"));
      setTypeToast("warning");
      setErrors(true);
      setTriggerToast(triggerToast + 1);
      return;
    }
    if (password !== confirmPassword) {
      setToastMessage(t("auth.changePassword.passwordsNotMatch"));
      setTypeToast("warning");
      setErrors(true);
      setTriggerToast(triggerToast + 1);
      return;
    }
    if (!guid) {
      setToastMessage(t("auth.changePassword.guidNotFound"));
      setTypeToast("warning");
      setErrors(true);
      setTriggerToast(triggerToast + 1);
      return;
    }
    try {
      setErrors(false);
      setLoading(true);
      const response = await userCustomerAuthenticationServices.changePassword({
        guid: guid,
        password: password,
        forgottenPassword: true,
      });

      if (response.status !== 200) {
        if (response.data.title) {
          setToastMessage(response.data.title);
        } else {
          setToastMessage(response.data);
        }

        setLoading(false);
        setErrors(true);
        setTypeToast("error");
        setTriggerToast(triggerToast + 1);
        return;
      }

      setLoading(false);
      setChangedPassword(true);
      setSuccessMessageReturnAPI(
        response.data?.message || String(response.data),
      );
    } catch {
      setToastMessage(t("common.unexpectedError"));
      setLoading(false);
      setErrors(false);
      setTypeToast("error");
      setTriggerToast(triggerToast + 1);
      return;
    }
  };

  return (
    <>
      <LeftFormContainer>
        <SimpleToast
          trigger={triggerToast}
          id="simpleToastCertificate"
          title={t("common.invalidPassword")}
          description={toastMessage}
          type={typeToast}
          handleCloseToast={() => setTriggerToast(0)}
        />

        {ChangePasswordStepOne(
          password,
          onChangePassword,
          confirmPassword,
          setConfirmPassword,
          showPassword,
          setShowPassword,
          errors,
          statusPassword,
          handleSubmit,
          t,
        )}

        {loading && (
          <LoadingContainer>
            <LoadingComponent loading={loading} />
          </LoadingContainer>
        )}
      </LeftFormContainer>
    </>
  );
};
